import React, { useState } from 'react';
import { Casa } from '../../types';
import { useTareas } from '../../context/TareasContext';
import { usePlus } from '../../context/PlusContext';
import { Plus, X } from 'lucide-react';
import { BotonesModal, Campo, ICONOS_CASA, MensajeError, ModalBase, claseInput } from './ui';

interface Props {
  casa?: Casa;
  onClose: () => void;
  onCreada?: (casaId: string) => void;
}

/** Nombre e icono de una casa. Al crearla se apuntan también quienes viven en ella. */
export const ModalCasa: React.FC<Props> = ({ casa, onClose, onCreada }) => {
  const { crearCasa, actualizarCasa } = useTareas();
  const { ofrecerSiEsLimite } = usePlus();
  const [nombre, setNombre] = useState(casa?.nombre ?? '');
  const [icono, setIcono] = useState(casa?.icono ?? ICONOS_CASA[0]);
  const [otros, setOtros] = useState<string[]>(['']);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cambiarOtro = (i: number, v: string) => setOtros(prev => prev.map((x, j) => (j === i ? v : x)));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) { setError('Ponle un nombre a la casa.'); return; }
    const nombres = otros.map(o => o.trim()).filter(Boolean);
    const repetido = nombres.find((n, i) => nombres.findIndex(m => m.toLowerCase() === n.toLowerCase()) !== i);
    if (repetido) { setError(`«${repetido}» está repetido.`); return; }
    setGuardando(true);
    setError('');
    try {
      if (casa) {
        await actualizarCasa(casa.id, { nombre, icono });
        onClose();
      } else {
        const id = await crearCasa({ nombre, icono }, nombres);
        onClose();
        onCreada?.(id);
      }
    } catch (err) {
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  return (
    <ModalBase titulo={casa ? 'Editar casa' : 'Nueva casa'} onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />
        <Campo label="Nombre *">
          <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Casa, Piso de la playa, Casa de mamá…" className={claseInput} autoFocus />
        </Campo>

        <Campo label="Icono">
          <div className="flex flex-wrap gap-1.5">
            {ICONOS_CASA.map(i => (
              <button key={i} type="button" onClick={() => setIcono(i)} aria-pressed={icono === i}
                className={`w-10 h-10 rounded-lg text-xl border transition ${
                  icono === i
                    ? 'border-lumbre-500 bg-lumbre-50 dark:bg-lumbre-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                }`}>
                {i}
              </button>
            ))}
          </div>
        </Campo>

        {!casa && (
          <Campo label="¿Quién más vive en ella?"
            ayuda="Tú ya estás incluido. Basta con el nombre (los niños no necesitan cuenta); a quien use la app podrás invitarle con un enlace.">
            <div className="space-y-2">
              {otros.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={o} onChange={e => cambiarOtro(i, e.target.value)}
                    onKeyDown={e => {
                      // Intro en el último añade otra fila en vez de enviar
                      if (e.key === 'Enter' && i === otros.length - 1 && o.trim()) {
                        e.preventDefault();
                        setOtros(prev => [...prev, '']);
                      }
                    }}
                    placeholder="Nombre" className={claseInput} />
                  {otros.length > 1 && (
                    <button type="button" onClick={() => setOtros(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Quitar" className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50
                                                     dark:hover:bg-red-900/20 transition">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setOtros(prev => [...prev, ''])}
                className="inline-flex items-center gap-1 text-sm font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
                <Plus className="w-4 h-4" /> Otra persona
              </button>
            </div>
          </Campo>
        )}

        <BotonesModal guardando={guardando} texto={casa ? 'Guardar cambios' : 'Crear casa'} onClose={onClose} />
      </form>
    </ModalBase>
  );
};
